import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchApiData } from '../../Utils/ApiCalls';
import { CONVERT_TO_ENTERPRISE, GET_CATEGORIES_ON_FALCON, GET_OPTIONAL_FEATURES, UPDATE_ENTERPRISE } from '../../urls';
import {
  Button,
  Input,
  Select,
  showToast,
  Checkbox,
  RadioGroup,
  Typography,
  Tooltip
} from '@attentive-platform/stem-ui';

import SearchComponent from '../SearchComponent';
import useFormStatus from '../../Hooks/useFormStatus';
import {
  BILLING_FREQUENCY,
  BILLING_FREQUENCY_ENUM,
  CHECHBOX_FIELDS,
  CONTRACT_INFO_FIELDS,
  COUNTRY_CODE,
  INITIAL_FORM_DATA,
  CATEGORY_PRICING_TYPES
} from '../../Constants/Constants';
import { GROUP_INPUT_DATA } from '../../Constants/Constants';
import {
  transformContractData,
  isValidPostalCode,
  setContractInfoData,
  setInitialFormData
} from '../../Utils/HelperFunctions';
import DeleteIcon from '../../Assets/DeleteIcon';

const EnterpriseConversionForm = ({
  updateEnterprise,
  enterpriseAttributes,
  billingDetails,
  enterpriseContract,
  platformAccess,
  email,
  enterpriseOptionalFeatures,
  totalUsers,
  showCategoryPricing,
  setEmail
}) => {
  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  const [contractInfo, setContractInfo] = useState({});
  const [formData, setFormData] = useState({});
  const [isManual, setIsManual] = useState(true);
  const [isInvalidZip, setIsInvalidZip] = useState(false);
  const [defaultReport, setDefaultReport] = useState('');
  const [inputValue, setInputValue] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionalFeatures, setOptionalFeatures] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const getOptionalFeatures = async () => {
    const response = await fetchApiData({
      endpoint: GET_OPTIONAL_FEATURES,
      apiRequest: 'GET'
    });
    setOptionalFeatures(response?.data);
  };

  useEffect(() => {
    const fetchOptionalFeatures = async () => {
      if (updateEnterprise) {
        const response = await fetchApiData({
          endpoint: GET_OPTIONAL_FEATURES,
          apiRequest: 'GET'
        });

        const existingFeatures = enterpriseOptionalFeatures || [];
        const newFeatures = response?.data || [];

        const existingFeaturesMap = new Map(existingFeatures.map(feature => [feature.identifier, feature]));

        const mergedFeatures = [...existingFeatures];
        newFeatures.forEach(newFeature => {
          if (!existingFeaturesMap.has(newFeature.identifier)) {
            mergedFeatures.push({
              ...newFeature,
              is_selected: false
            });
          }
        });

        setOptionalFeatures(mergedFeatures);
      } else {
        await getOptionalFeatures();
      }
    };
    fetchOptionalFeatures();
  }, [updateEnterprise, enterpriseOptionalFeatures]);

  useEffect(() => {
    const initializeFormData = () => {
      const initialData = setInitialFormData({
        email,
        enterpriseAttributes,
        platformAccess
      });
      setDefaultReport(initialData?.default_report);
      setFormData({
        ...initialData,
        total_users: totalUsers
      });
      const selectedOptions = initialData?.reports_given?.map(report => {
        const label = report?.name;
        const value = report?.id;
        return { label, value };
      });
      setSelectedOptions(selectedOptions);
      setInputValue(selectedOptions.map(option => option?.value));
      if (updateEnterprise) {
        if (!enterpriseAttributes?.is_pilot) {
          // Company is non pilot and company has contract
          // User cannot edit contract details and platform end date
          if (Object.keys(enterpriseContract || {}).length > 0) {
            setIsManual(false);
          }
        }
      }
    };
    const initialiseContractData = () => {
      const initData = setContractInfoData({
        enterpriseContract,
        billingDetails
      });
      setContractInfo(initData);
    };
    setSelectedCategories(Object.keys(enterpriseContract?.trade_wise_pricing || {}).filter(key => key !== 'others'));

    initializeFormData();
    initialiseContractData();
  }, [email, enterpriseAttributes, platformAccess, billingDetails, enterpriseContract, updateEnterprise, totalUsers]);

  const handleSubmit = e => {
    updateEnterpriseContract(e);
  };

  const updateEnterpriseContract = async e => {
    e.preventDefault();
    if (!isValidPostalCode(contractInfo.zip, contractInfo.country) && !isManual) {
      window.scrollTo(0, 0);
      setIsInvalidZip(true);
      return;
    }
    const newFormData = structuredClone(formData);
    const { end_date, total_users, email: formDataEmail, ...ent_attributes } = newFormData;
    const {
      address,
      city,
      zip,
      country,
      addendum_sheet_cost,
      aerial_contract_amount,
      billing_email,
      billing_frequency,
      bpt_contract_amount,
      company_name,
      manual_per_acre_cost,
      manual_threshold_acres,
      manual_worksheet_cost,
      per_acre_cost,
      remeasurement_contract_amount,
      remeasurement_per_acre_cost,
      start_date: contractStartDate,
      end_date: contractEndDate,
      workable_sheet_cost,
      trade_wise_pricing
    } = transformContractData(contractInfo);

    const data = {
      billing_details: !isManual
        ? {
            name: company_name,
            address,
            city,
            zip,
            country,
            email: billing_email?.toLowerCase()
          }
        : null,
      email: formDataEmail?.toLowerCase(),
      total_users: parseFloat(total_users || 0),
      enterprise_attributes: {
        ...ent_attributes,
        reports_given: inputValue,
        default_report: defaultReport
      },
      platform_access: {
        end_date
      },

      optional_feature_costs: optionalFeatures,

      enterprise_contract: !isManual
        ? {
            addendum_sheet_cost,
            billing_frequency,
            bpt_contract_amount,
            manual_worksheet_cost,
            start_date: contractStartDate + 'T00:00:00+05:30',
            end_date: contractEndDate + 'T00:00:00+05:30',
            workable_sheet_cost,
            ...(showCategoryPricing && { trade_wise_pricing }),
            manual_per_acre_cost,
            manual_threshold_acres,
            aerial_contract_amount,
            per_acre_cost,
            remeasurement_contract_amount,
            remeasurement_per_acre_cost
          }
        : null
    };

    const res = await fetchApiData({
      endpoint: updateEnterprise ? UPDATE_ENTERPRISE : CONVERT_TO_ENTERPRISE,
      data
    });
    handleApiResult(res);
    if (res.error === null) {
      setFormData({ ...INITIAL_FORM_DATA, total_users: 0 });
      setContractInfo(CONTRACT_INFO_FIELDS);
      setIsManual(true);
    } else if (res.error) {
      showToast(res.error, 'error');
    }
  };

  const showBillingInfo = !isManual && !formData?.is_pilot;

  return (
    <form onSubmit={handleSubmit} className='flex gap-6 w-full min-h-0 items-start'>
      {/* left section */}
      <div className={`${showBillingInfo ? 'flex-1' : 'w-1/2'} bg-white rounded-2xl shadow-md p-6`}>
        <div className='border-b border-gray-200 pb-2 mb-3'>
          <Typography variant='h6-medium'>Conversion/Enterprise Conversion</Typography>
        </div>
        <EnterpiseConversion
          successMessage={successMessage}
          errorMessage={errorMessage}
          handleApiResult={handleApiResult}
          isManual={isManual}
          setIsManual={setIsManual}
          formData={formData}
          setFormData={setFormData}
          defaultReport={defaultReport}
          inputValue={inputValue}
          setDefaultReport={setDefaultReport}
          setInputValue={setInputValue}
          setContractInfo={setContractInfo}
          contractInfo={contractInfo}
          updateEnterprise={updateEnterprise}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          isPilot={enterpriseAttributes?.is_pilot}
          optionalFeatures={optionalFeatures}
          setOptionalFeatures={setOptionalFeatures}
          setEmail={setEmail}
        />
        {!showBillingInfo && (
          <div className='flex justify-end mt-2'>
            <Button color='primary' size='medium' variant='contained' type='submit' className='!text-base'>
              {updateEnterprise ? 'Update Contract' : 'Convert to Enterprise'}
            </Button>
          </div>
        )}
      </div>

      {/* right section(only for automated billing)*/}
      {showBillingInfo && (
        <div className='flex-1 bg-white rounded-2xl shadow-md p-6'>
          <div className='border-b border-gray-200 pb-2 mb-3'>
            <Typography variant='h6-medium'>Enterprise Billing Info</Typography>
          </div>
          <EnterpriseBillingInfo
            successMessage={successMessage}
            errorMessage={errorMessage}
            handleApiResult={handleApiResult}
            isInvalidZip={isInvalidZip}
            contractInfo={contractInfo}
            setContractInfo={setContractInfo}
            showCategoryPricing={showCategoryPricing}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            updateEnterprise={updateEnterprise}
          />
        </div>
      )}
    </form>
  );
};

const EnterpiseConversion = ({
  errorMessage,
  successMessage,
  isManual,
  setIsManual,
  formData,
  setFormData,
  setDefaultReport,
  setInputValue,
  setContractInfo,
  contractInfo,
  updateEnterprise,
  selectedOptions,
  setSelectedOptions,
  isPilot,
  defaultReport,
  optionalFeatures,
  setOptionalFeatures,
  setEmail
}) => {
  const handleDataChange = (e, field) => {
    if (field === 'is_pilot') {
      setIsManual(true);
    }
    const val = e.target.value;
    setFormData({ ...formData, [field]: val });
  };

  const handleCheckboxChange = (e, field) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  return (
    <div className='w-full'>
      {(errorMessage || successMessage) && (
        <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'} mb-3`} role='alert'>
          <Typography variant='body8'>{errorMessage || successMessage}</Typography>
        </div>
      )}
      <div className='mb-3'>
        <Input
          label='Admin email'
          type='email'
          fullWidth
          value={formData?.email}
          onChange={e => {
            if (setEmail) setEmail(e.target.value);
            handleDataChange(e, 'email');
            setContractInfo({ ...contractInfo, email: e.target.value });
          }}
          placeholder='Enter admin email'
          required={!updateEnterprise}
        />
      </div>
      <div className='mb-4'>
        <SearchComponent
          setInputValue={setInputValue}
          setDefaultReport={setDefaultReport}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          defaultReport={defaultReport}
        />
      </div>
      {GROUP_INPUT_DATA.map(({ key, inputs, showLabel, className }) => {
        const isTwoColumn = inputs.length === 2;

        return (
          <div key={key} className={isTwoColumn ? 'grid grid-cols-2 gap-4 mb-3' : 'mb-3'}>
            {inputs.map(({ valueKey, type, label, placeholder, ...props }) => {
              if (type === 'date') {
                return (
                  <div key={valueKey} className='grid grid-cols-[2fr_1fr] items-center gap-4 mb-3'>
                    <Typography variant='body8' className='text-sm font-medium text-gray-700'>
                      {label || placeholder}
                    </Typography>
                    <Input
                      type='date'
                      fullWidth
                      value={formData[valueKey]}
                      onChange={e => handleDataChange(e, valueKey)}
                      {...props}
                      placeholder='-'
                    />
                  </div>
                );
              }

              const isWideInput = valueKey === 'subdomain' || valueKey === 'callback_url';
              const inputValue = formData[valueKey] || '';
              const showTooltip = isWideInput && inputValue;

              return (
                <div
                  key={valueKey}
                  className={`grid ${isWideInput ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[3fr_2fr]'} items-center gap-2 `}>
                  <Typography variant='body8'>{label || placeholder}</Typography>
                  {showTooltip ? (
                    <Tooltip title={inputValue} placement='top'>
                      <div className='w-full'>
                        <Input
                          type={type}
                          fullWidth
                          value={inputValue}
                          onChange={e => handleDataChange(e, valueKey)}
                          {...props}
                          placeholder={isWideInput ? '-' : ''}
                        />
                      </div>
                    </Tooltip>
                  ) : (
                    <Input
                      type={type}
                      fullWidth
                      value={inputValue}
                      onChange={e => handleDataChange(e, valueKey)}
                      {...props}
                      placeholder={isWideInput ? '-' : ''}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {optionalFeatures && optionalFeatures.length > 0 && (
        <div className='border rounded mb-3 bg-white max-h-[200px] overflow-y-scroll'>
          <div className='flex items-center justify-between font-semibold border-b pl-2 bg-gray-100 rounded-t mb-2'>
            <div className='flex items-center'>
              <Checkbox
                checked={optionalFeatures.every(feature => feature.is_selected)}
                onChange={e => {
                  const updated = optionalFeatures.map(feature => ({
                    ...feature,
                    is_selected: e.target.checked
                  }));
                  setOptionalFeatures(updated);
                }}
                className='!mr-0 !pr-0'
              />
              <Typography variant='body8' className='ml-2'>
                Features
              </Typography>
            </div>
            <div className='w-1/4 flex items-center'>
              <Typography variant='body8'>Pricing</Typography>
            </div>
          </div>
          <div className='pl-2'>
            {optionalFeatures.map((feature, index) => (
              <div className='flex items-center justify-between ' key={feature.identifier}>
                <div className='flex items-center mb-2'>
                  <Checkbox
                    checked={feature.is_selected}
                    onChange={e => {
                      const updated = [...optionalFeatures];
                      updated[index].is_selected = e.target.checked;
                      setOptionalFeatures(updated);
                    }}
                    className='mr-0 pr-0'
                  />
                  <Typography variant='body8' className='ml-2'>
                    {feature.name}
                  </Typography>
                </div>
                <div className='w-1/4 flex items-center'>
                  <Input
                    type='number'
                    value={feature.cost}
                    onChange={e => {
                      const updated = [...optionalFeatures];
                      updated[index].cost = e.target.value;
                      setOptionalFeatures(updated);
                    }}
                  />
                  <Typography variant='body8' className='mr-3 ml-2'>
                    $
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='flex flex-wrap mb-3'>
        {CHECHBOX_FIELDS.map((field, index) => (
          <div className={`flex items-center ${index > 0 ? 'ml-4' : ''}`} key={field}>
            <Checkbox
              checked={formData[field]}
              onChange={e => handleCheckboxChange(e, field)}
              disabled={field === 'is_pilot' && !isPilot && updateEnterprise}
              className='!mr-0 !pr-0'
            />
            <Typography variant='body8' className=''>
              {field.replace(/_/g, ' ').charAt(0).toUpperCase() + field.replace(/_/g, ' ').slice(1)}
            </Typography>
          </div>
        ))}
      </div>

      <div className='border-y-2 w-full mb-3 py-2'>
        <div className='flex items-center gap-4'>
          <Typography variant='body8' className='font-medium'>
            Billing type:
          </Typography>
          <RadioGroup
            value={isManual ? 'manual' : 'automated'}
            onChange={(event, newValue) => setIsManual(newValue === 'manual')}
            radioGroup={[
              { label: 'Manual Billing', value: 'manual' },
              ...(!formData?.is_pilot ? [{ label: 'Automated Billing', value: 'automated' }] : [])
            ]}
            row
          />
        </div>
      </div>
    </div>
  );
};

const CategoryRow = React.memo(
  ({ categoryId, categoryName, pricing, isDeletable = true, onChangePricing, onDeleteCategory }) => (
    <div className='p-2 hover:bg-gray-50 transition-colors'>
      <div className='grid grid-cols-4 gap-2 items-center'>
        <Typography variant='body8' className='font-medium text-gray-800 truncate' title={categoryName}>
          {categoryName}
        </Typography>
        <Input
          type='number'
          min={1}
          step={0.01}
          placeholder='Fresh'
          value={pricing?.[CATEGORY_PRICING_TYPES.FRESH] ?? ''}
          onChange={e => onChangePricing(categoryId, CATEGORY_PRICING_TYPES.FRESH, e.target.value)}
          required
          fullWidth
        />
        <div>
          <Input
            type='number'
            min={1}
            step={0.01}
            placeholder='Addendum'
            value={pricing?.[CATEGORY_PRICING_TYPES.ADDENDUM] ?? ''}
            onChange={e => onChangePricing(categoryId, CATEGORY_PRICING_TYPES.ADDENDUM, e.target.value)}
            required
            fullWidth
          />
        </div>
        <div className='flex justify-center'>
          <Button
            variant='text'
            onClick={() => onDeleteCategory(categoryId)}
            disabled={!isDeletable}
            className='!min-w-0 !p-1'
            title='Delete category'>
            <DeleteIcon fill={isDeletable ? 'red' : 'gray'} />
          </Button>
        </div>
      </div>
    </div>
  )
);

const EnterpriseBillingInfo = ({
  errorMessage,
  successMessage,
  isInvalidZip,
  contractInfo,
  setContractInfo,
  showCategoryPricing,
  selectedCategories,
  setSelectedCategories,
  updateEnterprise
}) => {
  const [categories, setCategories] = useState([]);
  const [fetchingCategories, setFetchingCategories] = useState(false);

  const categoriesOptions = useMemo(() => {
    return categories.map(category => ({
      value: category.id,
      label: category.name
    }));
  }, [categories]);

  const handleDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const handleCategoryPricingChange = useCallback(
    (categoryId, type, value) => {
      setContractInfo(prev => ({
        ...prev,
        trade_wise_pricing: {
          ...prev.trade_wise_pricing,
          [categoryId]: {
            ...prev.trade_wise_pricing[categoryId],
            [type]: value
          }
        }
      }));
    },
    [setContractInfo]
  );

  const handleDeleteCategory = useCallback(
    categoryId => {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));

      setContractInfo(prev => {
        const newPricing = { ...prev.trade_wise_pricing };
        delete newPricing[categoryId];
        return {
          ...prev,
          trade_wise_pricing: newPricing
        };
      });
    },
    [setContractInfo, setSelectedCategories]
  );

  const setDates = date => {
    const year = parseInt(date?.substring(0, 4)) + 1;
    const endDate = year + date?.substring(4);
    setContractInfo({ ...contractInfo, start_date: date, end_date: endDate });
  };

  const fetchCategories = useCallback(async () => {
    try {
      setFetchingCategories(true);
      const { data } = await fetchApiData({
        endpoint: GET_CATEGORIES_ON_FALCON,
        apiRequest: 'GET',
        open: true
      });
      setCategories(data || []);
    } catch (e) {
      setCategories([]);
      showToast(e.message, 'error');
    } finally {
      setFetchingCategories(false);
    }
  }, []);

  useEffect(() => {
    if (showCategoryPricing) {
      fetchCategories();
    }
  }, [showCategoryPricing, fetchCategories]);

  const countryItems = [
    { label: 'United States', value: COUNTRY_CODE.USA },
    { label: 'Canada', value: COUNTRY_CODE.CANADA },
    { label: 'Australia', value: COUNTRY_CODE.AUSTRALIA }
  ];

  const billingFrequencyItems = [
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.QUATERLY], value: BILLING_FREQUENCY_ENUM.QUATERLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.MONTHLY], value: BILLING_FREQUENCY_ENUM.MONTHLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.HALF_YEARLY], value: BILLING_FREQUENCY_ENUM.HALF_YEARLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.ANNUAL], value: BILLING_FREQUENCY_ENUM.ANNUAL }
  ];

  return (
    <div className='w-full'>
      {(errorMessage || successMessage || isInvalidZip) && (
        <div className={`alert ${errorMessage || isInvalidZip ? 'alert-danger' : 'alert-success'} mb-3`} role='alert'>
          <Typography variant='body8'>{errorMessage || successMessage || 'Please enter valid zip code'}</Typography>
        </div>
      )}

      <div className='grid grid-cols-2 gap-x-4 gap-y-2 mb-2'>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Company Name
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.company_name}
            onChange={e => handleDataChange(e.target.value, 'company_name')}
            placeholder='Enter company name'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Company Billing Email
          </Typography>
          <Input
            type='email'
            fullWidth
            value={contractInfo.billing_email}
            onChange={e => handleDataChange(e.target.value, 'billing_email')}
            placeholder='Enter company billing email'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Address
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.address}
            onChange={e => handleDataChange(e.target.value, 'address')}
            placeholder='Enter address'
            required
          />
        </div>
        <div className='mb-1'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            City
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.city}
            onChange={e => handleDataChange(e.target.value, 'city')}
            placeholder='Enter city'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Zipcode
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.zip}
            onChange={e => handleDataChange(e.target.value, 'zip')}
            placeholder='Enter zipcode'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Country
          </Typography>
          <Select
            fullWidth
            value={contractInfo.country}
            onChange={value => handleDataChange(value, 'country')}
            items={countryItems}
            required
          />
        </div>
      </div>
      <Typography variant='h6-medium' className='mb-3'>
        Enterprise Contract Info
      </Typography>

      <div className='grid grid-cols-2 gap-4 mb-3'>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract Start Date
          </Typography>
          <Input
            type='date'
            fullWidth
            value={contractInfo.start_date}
            onChange={e => {
              setDates(e.target.value);
            }}
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract End Date
          </Typography>
          <Input
            type='date'
            fullWidth
            value={contractInfo.end_date}
            onChange={e => {
              setContractInfo({ ...contractInfo, end_date: e.target.value });
            }}
            required
            placeholder='-'
          />
        </div>
      </div>
      <div className='mb-3'>
        <Typography variant='body8' className='text-sm font-medium text-gray-700'>
          Billing Frequency
        </Typography>
        <Select
          fullWidth
          value={contractInfo?.billing_frequency}
          onChange={value => handleDataChange(value, 'billing_frequency')}
          items={billingFrequencyItems}
          required
          placeholder='-'
        />
      </div>
      <div className='mb-3'>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Aerial Contract Amount ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.aerial_contract_amount}
            onChange={e => handleDataChange(e.target.value, 'aerial_contract_amount')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Aerial Per Acre Cost ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.per_acre_cost}
            onChange={e => handleDataChange(e.target.value, 'per_acre_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Per Acre Imagery Cost for Manual Measurement, Beyond Threshold Acres ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.manual_per_acre_cost}
            onChange={e => handleDataChange(e.target.value, 'manual_per_acre_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Threshold Acres for Imagery cost in Manual Measurements ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            defaultValue='20'
            fullWidth
            value={contractInfo.manual_threshold_acres}
            onChange={e => handleDataChange(e.target.value, 'manual_threshold_acres')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Aerial Remeasurement Contract Amount ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.remeasurement_contract_amount}
            onChange={e => handleDataChange(e.target.value, 'remeasurement_contract_amount')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Aerial Remeasurement Per Acre Cost ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.remeasurement_per_acre_cost}
            onChange={e => handleDataChange(e.target.value, 'remeasurement_per_acre_cost')}
            required
            placeholder='-'
          />
        </div>
      </div>
      <div className='mb-3'>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract Amount for BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.bpt_contract_amount}
            onChange={e => handleDataChange(e.target.value, 'bpt_contract_amount')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Workable Sheet Cost BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.workable_sheet_cost}
            onChange={e => handleDataChange(e.target.value, 'workable_sheet_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-4 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Addendum Sheet Cost BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.addendum_sheet_cost}
            onChange={e => handleDataChange(e.target.value, 'addendum_sheet_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-4 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Workable Sheet in Manual Measurement for BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.manual_worksheet_cost}
            onChange={e => handleDataChange(e.target.value, 'manual_worksheet_cost')}
            required
            placeholder='-'
          />
        </div>
      </div>

      {showCategoryPricing && (
        <>
          <Typography variant='h6-medium' className='mt-4 mb-3'>
            Categories Info
          </Typography>

          <Select
            allowClear
            allowClearIcon
            size='large'
            multiple
            isLoading={fetchingCategories}
            color='secondary'
            items={categoriesOptions}
            value={selectedCategories}
            onChange={setSelectedCategories}
            placeholder='Select Categories'
            variant='std-filled'
            className='mb-4'
          />

          <div className='mb-3'>
            <Typography variant='h6-medium' className='mb-3'>
              Category Pricing ($)
            </Typography>
            <div className='bg-white border rounded-lg overflow-hidden'>
              <div className='bg-gray-50 px-2 pt-2 border-b'>
                <div className='grid grid-cols-4 gap-2 font-medium text-gray-700'>
                  <Typography variant='subtitle2-medium'>Category</Typography>
                  <Typography variant='subtitle2-medium'>Fresh</Typography>
                  <Typography variant='subtitle2-medium'>Addendum</Typography>
                  <Typography variant='subtitle2-medium'>Action</Typography>
                </div>
              </div>
              <div className='divide-y'>
                {selectedCategories.map(categoryId => {
                  const category = categories.find(cat => cat.id === categoryId);
                  const pricing = contractInfo?.trade_wise_pricing?.[categoryId] || {};
                  return (
                    <CategoryRow
                      key={categoryId}
                      categoryId={categoryId}
                      categoryName={category?.name}
                      pricing={pricing}
                      onChangePricing={handleCategoryPricingChange}
                      onDeleteCategory={handleDeleteCategory}
                    />
                  );
                })}
                <CategoryRow
                  categoryId='others'
                  categoryName='Others'
                  pricing={contractInfo.trade_wise_pricing?.['others'] || {}}
                  isDeletable={false}
                  onChangePricing={handleCategoryPricingChange}
                  onDeleteCategory={handleDeleteCategory}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <div className='flex justify-end mt-3'>
        <Button color='primary' size='medium' variant='contained' type='submit' className='!text-base'>
          {updateEnterprise ? 'Update Contract' : 'Convert to Enterprise'}
        </Button>
      </div>
    </div>
  );
};

export default EnterpriseConversionForm;
// for update contract
export { EnterpriseBillingInfo, EnterpiseConversion };
